import type { PostingEvent } from "@/lib/api/catalogue";
import type { ApplicationEvent } from "@/lib/api/envoy";
import { lookup } from "@/lib/lookup";
import { postedInFull } from "./dates";
import { field, text, count } from "./payload";
import type { RecordTone } from "./record";
import { skipReasonWords } from "./status";

/** What each kind of event is called on the record, and its detail line. */

export const ENVOY_KIND: Record<string, [string, RecordTone]> = {
    MATCHED: ["Matched", "match"],
    QUEUED: ["Queued", "queue"],
    UNQUEUED: ["Taken out", "quiet"],
    TAILOR_STARTED: ["Tailoring", "queue"],
    TAILORED: ["Tailored", "tailor"],
    TAILOR_FAILED: ["Tailor failed", "fault"],
    BLOCKED: ["Handed to you", "queue"],
    FAILED: ["Failed", "fault"],
    APPLIED: ["Applied", "sent"],
    SKIPPED: ["Skipped", "quiet"],
    CLOSED: ["Closed", "quiet"],
    OUTCOME_RECORDED: ["Outcome", "sent"],
};

export const CATALOGUE_KIND: Record<string, string> = {
    DISCOVERED: "Discovered",
    CHANGED: "Changed",
    CLOSED: "Closed",
    REOPENED: "Listed again",
    ANALYZED: "Analyzed",
};

/** A kind no table names yet, as words: "TAILOR_FAILED" reads "Tailor failed". */
export function kindWords(kind: string): string {
    const words = kind.replaceAll("_", " ").toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

const TAILOR_FAILURE: Record<string, string> = {
    FAILED: "the call, the check or the render failed",
    TIMED_OUT: "the tailor ran out of time",
    OUT_OF_TAILORS: "no tailors were left this period, so it waits for one",
    EXHAUSTED: "no tailors were left this period, so it waits for one",
    SLOWED: "the tailor was asked to slow down, so it tries again in a few minutes",
    REFUSED: "the model would not write it",
};

/**
 * Why the last tailor failed, in words, from the latest failure on the
 * application's record: "the model would not write it". Null before one is
 * on it.
 */
export function tailorFailure(events: readonly ApplicationEvent[]): string | null {
    const last = events
        .filter((event) => event.kind === "FAILED" || event.kind === "TAILOR_FAILED")
        .reduce<ApplicationEvent | null>(
            (latest, event) => (latest && latest.seq > event.seq ? latest : event),
            null,
        );
    if (!last) return null;
    return lookup(TAILOR_FAILURE, text(last.payload, "reason")) ?? "the tailor did not finish";
}

const OUTCOME_WORDS: Record<string, string> = {
    INTERVIEWING: "interviewing",
    OFFER: "an offer",
    REJECTED: "not this time",
    WITHDRAWN: "withdrawn",
};

/** What changed between two reads of a posting, as the fields a person would notice. */
function changedFields(before: unknown, after: unknown): string[] {
    const differs = (name: string) =>
        JSON.stringify(field(before, name) ?? null) !== JSON.stringify(field(after, name) ?? null);
    const named: [string, string][] = [
        ["title", "the title"],
        ["location", "the location"],
        ["pay", "the pay"],
        ["work_setting", "how it is worked"],
        ["apply_url", "the apply page"],
        ["description", "the text"],
        ["keywords", "what it asks for"],
    ];
    return named.filter(([name]) => differs(name)).map(([, words]) => words);
}

function listed(items: string[]): string {
    if (items.length <= 1) return items.join("");
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function catalogueDetail(
    event: PostingEvent,
    previous: PostingEvent | undefined,
    timeZone?: string,
): string {
    const payload = event.payload;
    switch (event.kind) {
        case "DISCOVERED": {
            const company = text(payload, "company");
            const external = text(payload, "external_id");
            const posted = text(payload, "posted_at");
            const found = company ? `Found at ${company}` : "Found";
            const asPosting = external ? ` as posting ${external}` : "";
            const went =
                posted && !Number.isNaN(new Date(posted).getTime())
                    ? ` The posting says it went up on ${postedInFull(posted, timeZone)}.`
                    : "";
            return `${found}${asPosting}.${went}`;
        }
        case "CHANGED": {
            const fields = previous ? changedFields(previous.payload, payload) : [];
            return fields.length > 0
                ? `The posting changed ${listed(fields)}.`
                : "The posting changed.";
        }
        case "CLOSED":
            return "The posting closed.";
        case "REOPENED":
            return "The posting opened again.";
        default:
            return "";
    }
}

export function envoyDetail(
    event: ApplicationEvent,
    modelName: (model: string) => string,
    queuedBefore: boolean,
): string {
    const payload = event.payload;
    switch (event.kind) {
        case "MATCHED": {
            const asked = count(payload, "keywords_asked");
            const covered = count(payload, "keywords_covered");
            const version = count(payload, "envoy_version");
            const fit =
                asked && asked > 0
                    ? ` Your resume said ${covered ?? 0} of the ${asked} keywords it names.`
                    : "";
            return `It fits what your envoy looks for${version ? `, as its settings stood at version ${version}` : ""}.${fit}`;
        }
        case "QUEUED":
            return text(payload, "by") === "envoy"
                ? "Your envoy put it in line under its order to find and tailor."
                : queuedBefore
                  ? "You put it back in your queue."
                  : "You put it in your queue. Your envoy works it ahead of what it finds itself.";
        case "UNQUEUED":
            return "Taken out of your queue before its tailor started.";
        case "TAILOR_STARTED":
            return "The tailor started writing the resume toward this posting.";
        case "TAILORED": {
            const model = text(payload, "model");
            const who =
                text(payload, "tailor") === "keyword"
                    ? "The keyword tailor"
                    : model
                      ? modelName(model)
                      : "The tailor";
            return `${who} wrote the resume toward this posting. It was checked against your resume before it was kept.`;
        }
        case "TAILOR_FAILED": {
            const reason = text(payload, "reason");
            const why = lookup(TAILOR_FAILURE, reason) ?? "the tailor did not finish";
            return `No resume this time: ${why}.`;
        }
        case "BLOCKED":
            return "The tailor gave up, so it was handed to you to send with your saved resume or skip.";
        case "FAILED": {
            const reason = text(payload, "reason");
            const why = lookup(TAILOR_FAILURE, reason) ?? "the tailor did not finish";
            return `The tailor is not tried again: ${why}. Nothing was charged. You can still send it with your saved resume.`;
        }
        case "APPLIED": {
            const how =
                text(payload, "via") === "extension"
                    ? "The extension filled the form and you pressed Submit"
                    : "You recorded it sent";
            const what = text(payload, "resume_id")
                ? "with the tailored resume"
                : "with your saved resume";
            return `${how}, ${what}.`;
        }
        case "SKIPPED":
            return `You skipped it: ${skipReasonWords(text(payload, "reason"))}. Your envoy never matches it again.`;
        case "CLOSED":
            return "The posting closed before an application went in.";
        case "OUTCOME_RECORDED": {
            const outcome = text(payload, "outcome");
            const words = outcome ? (lookup(OUTCOME_WORDS, outcome) ?? outcome.toLowerCase()) : "";
            return `You recorded what the company said: ${words}.`;
        }
        default:
            return "";
    }
}
